"use client";

import { Logo } from "@/components/ui/logo";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/libs/cn";
import { StartButton } from "@/features/landing/ui/start-button";
import { ReportExampleButton } from "@/features/landing/ui/report-example-button";
import { useCommonTranslation } from "@/features/i18n/libs/useCommonTranslation";
import { useEffect, useRef } from "react";
import { EMAIL_SUPPORT } from "@/shared/consts";

export const staticFooterVisibility = {
  isVisible: false,
  subscribers: new Set<() => void>(),

  subscribe(callback: () => void) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  },

  setVisible(visible: boolean) {
    if (this.isVisible !== visible) {
      this.isVisible = visible;
      this.subscribers.forEach((callback) => callback());
    }
  },
};

function ResponsiveContainer({
  children,
  className = "",
  spacing = "none",
}: {
  children: React.ReactNode;
  className?: string;
  spacing?: "none" | "section";
}) {
  const spacingClasses = {
    none: "",
    section: "py-16 lg:py-20 xl:py-24 2xl:py-30",
  };

  return (
    <div
      className={cn(
        "px-4 lg:px-8 xl:px-16 2xl:px-16",
        spacingClasses[spacing],
        className,
      )}
    >
      {children}
    </div>
  );
}

function ResponsiveSection({
  children,
  className = "",
  spacing = "default",
}: {
  children: React.ReactNode;
  className?: string;
  spacing?: "default" | "large";
}) {
  const spacingClasses = {
    default: "mb-8 lg:mb-10 xl:mb-16 2xl:mb-20",
    large:
      "mb-16 mt-12 lg:mb-20 lg:mt-16 xl:mb-24 xl:mt-20 2xl:mb-30 2xl:mt-24",
  };

  return (
    <div className={cn(spacingClasses[spacing], className)}>{children}</div>
  );
}

function FooterLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      className="hover:text-text-primary text-md font-semibold leading-none text-text-secondary underline"
    >
      {children}
    </a>
  );
}

export function StaticFooter() {
  const { t } = useCommonTranslation();
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          staticFooterVisibility.setVisible(entry.isIntersecting);
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = footerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <ResponsiveSection>
      <ResponsiveContainer>
        <div
          ref={footerRef}
          className="grid grid-cols-1 gap-8 pb-[64px] pt-[32px] lg:grid-cols-3 lg:gap-12 lg:pb-[40px] lg:pt-0 xl:gap-16 xl:pb-[64px] 2xl:gap-20 2xl:pb-[72px]"
        >
          <div className="flex flex-col justify-between">
            <Logo variant="small" font="nunitoSans" />
            <Typography className="mt-4 text-md font-semibold text-text-secondary lg:mt-6 2xl:mt-8">
              © {new Date().getFullYear()} {t("landing:copyright")}
            </Typography>
          </div>

          <div className="flex flex-col gap-4">
            <FooterLink href="/privacy-policy">
              {t("landing:privacy_policy")}
            </FooterLink>
            <FooterLink href="/terms-of-use">
              {t("landing:terms_of_use")}
            </FooterLink>
            <FooterLink href={`mailto:${EMAIL_SUPPORT}`}>
              {t("landing:support")}
            </FooterLink>
          </div>

          <div className="flex items-center justify-center">
            <div className="flex flex-col gap-4">
              <StartButton />
              <ReportExampleButton variant="short" />
            </div>
          </div>
        </div>
      </ResponsiveContainer>
    </ResponsiveSection>
  );
}
